// ==============================================================================
// CONTEXTO DE AUTENTICAÇÃO E DADOS ADMINISTRATIVOS (PRODUÇÃO & LOCAL)
// ==============================================================================
// Em produção (VITE_APP_ENV=production):
// - Integração real com Supabase Auth (signInWithPassword, onAuthStateChange, signOut).
// - Validação obrigatória em public.admin_profiles (role === 'admin' e is_active === true).
// - Persistência real de leads, transições de status e anotações via adminApi PostgREST.
// - Tratamento de estado de carregamento (isLoading) para proteção de rotas no reload.
// Em ambiente local / testes unitários:
// - Suporte ao adapter mock de interface para manter testes automatizados isolados.
// ==============================================================================

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { CommercialStatus } from '@/domains/admin/types';
import { adminApi, type AdminLeadItem } from '@/services/adminApi';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { MOCK_ADMIN_USER, MOCK_INACTIVE_ADMIN_USER, MOCK_LEADS } from '@/mocks/mockData';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
}

export interface LoginResult {
  success: boolean;
  error?: string;
}

interface AdminAuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AdminUser | null;
  leads: AdminLeadItem[];
  isLeadsLoading: boolean;
  leadsError: string | null;
  login: (email: string, pass: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  refreshLeads: () => Promise<void>;
  updateLeadStatus: (leadId: string, status: CommercialStatus) => Promise<void>;
  updateLeadNote: (leadId: string, note: string) => Promise<void>;
}

const AUTH_STORAGE_KEY = 'admin_simulated_auth';
const AUTH_EMAIL_KEY = 'admin_simulated_email';

const isProduction =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_APP_ENV === 'production';

const AdminAuthContext = createContext<AdminAuthContextType | null>(null);

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<AdminUser | null>(null);
  const [leads, setLeads] = useState<AdminLeadItem[]>([]);
  const [isLeadsLoading, setIsLeadsLoading] = useState<boolean>(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);

  // Consulta e validação de perfil administrativo em public.admin_profiles
  const checkAdminProfile = useCallback(
    async (userId: string, userEmail: string): Promise<AdminUser | null> => {
      try {
        const { data, error } = await supabase
          .from('admin_profiles')
          .select('id, email, full_name, role, is_active')
          .eq('id', userId)
          .maybeSingle();

        if (error || !data) {
          return null;
        }

        if (data.role !== 'admin' || !data.is_active) {
          return null;
        }

        return {
          id: data.id,
          email: data.email || userEmail,
          name: data.full_name || 'Israel Costa',
          role: data.role,
          isActive: data.is_active,
        };
      } catch {
        return null;
      }
    },
    [],
  );

  // Carregamento de leads da API PostgREST
  const refreshLeads = useCallback(async () => {
    setIsLeadsLoading(true);
    setLeadsError(null);
    try {
      const data = await adminApi.getLeads();
      setLeads(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar leads.';
      setLeadsError(msg);
    } finally {
      setIsLeadsLoading(false);
    }
  }, []);

  // Inicialização e escuta do estado de autenticação
  useEffect(() => {
    let mounted = true;

    // Se estamos em ambiente local de mock (e não em produção nem com Supabase configurado)
    if (!isProduction && !isSupabaseConfigured) {
      if (typeof window !== 'undefined' && sessionStorage.getItem(AUTH_STORAGE_KEY) === 'true') {
        const email = sessionStorage.getItem(AUTH_EMAIL_KEY);
        if (email === MOCK_ADMIN_USER.email) {
          setIsAuthenticated(true);
          setUser({
            id: MOCK_ADMIN_USER.id,
            email: MOCK_ADMIN_USER.email,
            name: MOCK_ADMIN_USER.name,
            role: MOCK_ADMIN_USER.role,
            isActive: MOCK_ADMIN_USER.isActive,
          });
          setLeads(
            MOCK_LEADS.map((l) => ({
              ...l,
              leadName: l.name,
              leadEmail: l.email,
              leadPhone: l.phone,
              leadStatus: l.commercialStatus,
              companySize: '2 a 5 pessoas',
              overallTierLabel: 'Presença em construção',
              primaryPriorityName: l.primaryPriorityKey.replace('_', ' '),
              completedAt: l.createdAt,
              latestNote: l.notes,
              history: (l.history || []).map((h) => ({
                id: h.id,
                submissionId: h.leadId,
                leadId: h.leadId,
                fromStatus: h.oldValue,
                toStatus: h.newValue as CommercialStatus,
                notes: h.field === 'notes' ? h.newValue : undefined,
                changedBy: h.changedBy,
                createdAt: h.timestamp,
                timestamp: h.timestamp,
                field: h.field,
                oldValue: h.oldValue,
                newValue: h.newValue,
              })),
            })),
          );
        }
      }
      setIsLoading(false);
      return;
    }

    // Inicialização em Produção via Supabase Auth
    async function initAuth() {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (session?.user) {
          const profile = await checkAdminProfile(session.user.id, session.user.email || '');
          if (profile && mounted) {
            setUser(profile);
            setIsAuthenticated(true);
            refreshLeads();
          } else {
            await supabase.auth.signOut();
            if (mounted) {
              setUser(null);
              setIsAuthenticated(false);
            }
          }
        } else {
          if (mounted) {
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        console.error('Erro na inicialização da autenticação:', err);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    initAuth();

    // Escuta contínua das mudanças de autenticação
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsAuthenticated(false);
        setLeads([]);
        setIsLoading(false);
        return;
      }

      if (session?.user && event === 'SIGNED_IN') {
        const profile = await checkAdminProfile(session.user.id, session.user.email || '');
        if (profile && mounted) {
          setUser(profile);
          setIsAuthenticated(true);
          refreshLeads();
        } else {
          await supabase.auth.signOut();
          if (mounted) {
            setUser(null);
            setIsAuthenticated(false);
          }
        }
        if (mounted) {
          setIsLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [checkAdminProfile, refreshLeads]);

  // Função de Login
  const login = async (email: string, pass: string): Promise<LoginResult> => {
    const cleanEmail = email.trim().toLowerCase();

    // Simulação exclusiva para testes locais quando não em produção
    if (!isProduction && !isSupabaseConfigured) {
      await new Promise((res) => setTimeout(res, 100));

      if (cleanEmail === MOCK_ADMIN_USER.email.toLowerCase()) {
        if (!MOCK_ADMIN_USER.isActive) {
          return {
            success: false,
            error: 'Acesso negado: Perfil administrativo inativo no sistema.',
          };
        }

        setIsAuthenticated(true);
        setUser({
          id: MOCK_ADMIN_USER.id,
          email: MOCK_ADMIN_USER.email,
          name: MOCK_ADMIN_USER.name,
          role: MOCK_ADMIN_USER.role,
          isActive: MOCK_ADMIN_USER.isActive,
        });
        setLeads(
          MOCK_LEADS.map((l) => ({
            ...l,
            leadName: l.name,
            leadEmail: l.email,
            leadPhone: l.phone,
            leadStatus: l.commercialStatus,
            companySize: '2 a 5 pessoas',
            overallTierLabel: 'Presença em construção',
            primaryPriorityName: l.primaryPriorityKey.replace('_', ' '),
            completedAt: l.createdAt,
            latestNote: l.notes,
            history: (l.history || []).map((h) => ({
              id: h.id,
              submissionId: h.leadId,
              leadId: h.leadId,
              fromStatus: h.oldValue,
              toStatus: h.newValue as CommercialStatus,
              notes: h.field === 'notes' ? h.newValue : undefined,
              changedBy: h.changedBy,
              createdAt: h.timestamp,
              timestamp: h.timestamp,
              field: h.field,
              oldValue: h.oldValue,
              newValue: h.newValue,
            })),
          })),
        );
        try {
          sessionStorage.setItem(AUTH_STORAGE_KEY, 'true');
          sessionStorage.setItem(AUTH_EMAIL_KEY, MOCK_ADMIN_USER.email);
        } catch {
          // ignore
        }
        return { success: true };
      }

      if (cleanEmail === MOCK_INACTIVE_ADMIN_USER.email.toLowerCase()) {
        return {
          success: false,
          error: 'Acesso negado: Perfil administrativo inativo no sistema.',
        };
      }

      return {
        success: false,
        error: 'Credenciais de desenvolvimento não reconhecidas ou sem perfil administrativo.',
      };
    }

    // Login real de Produção com Supabase Auth
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: cleanEmail,
        password: pass,
      });

      if (authError || !authData.user) {
        return {
          success: false,
          error: 'E-mail ou senha incorretos.',
        };
      }

      // Validação mandatória em public.admin_profiles
      const profile = await checkAdminProfile(authData.user.id, authData.user.email || cleanEmail);
      if (!profile) {
        await supabase.auth.signOut();
        return {
          success: false,
          error: 'Acesso negado: Perfil sem privilégios de administrador ou inativo.',
        };
      }

      setUser(profile);
      setIsAuthenticated(true);
      await refreshLeads();
      return { success: true };
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Falha na comunicação com o servidor de autenticação.';
      return {
        success: false,
        error: message,
      };
    }
  };

  // Função de Logout
  const logout = async () => {
    setIsAuthenticated(false);
    setUser(null);
    setLeads([]);
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
      sessionStorage.removeItem(AUTH_EMAIL_KEY);
    } catch {
      // ignore
    }
    if (isSupabaseConfigured) {
      await supabase.auth.signOut().catch(() => {});
    }
  };

  // Atualização persistente de status de lead
  const updateLeadStatus = async (leadId: string, newStatus: CommercialStatus) => {
    const updatedLead = await adminApi.updateLeadStatus({ leadId, status: newStatus });
    setLeads((prev) =>
      prev.map((lead) => {
        if (lead.id !== leadId) return lead;
        return {
          ...lead,
          commercialStatus: newStatus,
          leadStatus: newStatus,
          history: updatedLead.history || lead.history,
        };
      }),
    );
  };

  // Atualização persistente de anotação de lead
  const updateLeadNote = async (leadId: string, note: string) => {
    const targetLead = leads.find((l) => l.id === leadId);
    const currentStatus = targetLead?.commercialStatus || 'novo';

    const newHistoryEntry = await adminApi.createLeadNote({
      leadId,
      currentStatus,
      notes: note,
    });

    setLeads((prev) =>
      prev.map((lead) => {
        if (lead.id !== leadId) return lead;
        return {
          ...lead,
          notes: note,
          latestNote: note,
          history: [newHistoryEntry, ...(lead.history || [])],
        };
      }),
    );
  };

  return (
    <AdminAuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        leads,
        isLeadsLoading,
        leadsError,
        login,
        logout,
        refreshLeads,
        updateLeadStatus,
        updateLeadNote,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth deve ser usado dentro de um AdminAuthProvider');
  }
  return context;
}
